import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Divider,
  Flex,
  Image,
  Skeleton,
  Text,
  useDisclosure,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {ChevronRightCustomizeIcon} from '@amblique/king-living/app/components/icons';
import {PIM_SQUARE_THUMBNAIL_PX} from '@amblique/king-living/app/components/configuration-selector/utils';
import {useProductDetailContentful} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {useCustomizeJourney} from '@amblique/king-living/app/hooks/use-customize-journey';
import {useCommittedCoverVariantProduct} from '@amblique/king-living/app/hooks/use-committed-cover-variant-product';
import {buildDefaultSelectionsFromData} from '@amblique/king-living/app/context/customize-journey-context';
import {computeCustomizeListTotal, computeCustomizeTotal} from '@amblique/king-living/app/utils/cart-utils';
import {ModeChangeConfirmationDialog} from '@amblique/king-living/app/components/configuration-selector/mode-change-confirmation-dialog';
import {resolveRecommendationNameFromProduct} from '@amblique/king-living/app/components/complex-customize/utils/options-grouping-ui-utils';
import {getCoverGroups} from '@amblique/king-living/app/utils/customize-selections';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';
import {getOptionsGroupingUIProcessed, sfccProductIdDataAttrs} from '@amblique/king-living/app/utils/product-utils';
import {handleRadioTileKeyDown} from '@amblique/king-living/app/utils/radio-keyboard';

/**
 * Turn spaced-hyphen separators in catalog names into commas for summary display
 * (e.g. "Standard Shelf - Natural Oak" → "Standard Shelf, Natural Oak").
 *
 * @param {string|null|undefined} value
 * @returns {string|null|undefined}
 */
function formatCustomizeSummaryLabel(value) {
  if (value == null || typeof value !== 'string') return value;
  return value.replace(/\s-\s/g, ', ');
}

/**
 * Flatten a SelectionsMap into an ordered list of display items for the summary.
 * Order: cover first, then option containers in their natural key order.
 *
 * @param {import('../context/customize-journey-context').SelectionsMap} selections
 * @param {string|null} currency
 * @param {Object|null} [product] - Used to resolve recommendation names when missing on slot objects
 * @param {Object|null} [optionPriceMap] - productId → after-promotion effective price (SCAPI
 *   productPromotions). Preferred over the seeded sale/price so the expanded set rows match
 *   the promotional pricing shown on the set-card total and the CTA. See `setOptionPriceMap`
 *   in `customize-journey-context.jsx` and `docs/product/pricing-architecture.md` §4d.
 * @param {Object|null} [listPriceMap] - productId → list (RRP) price for strikethrough.
 * @returns {Array<{key: string, thumbnail: string|null, recommendationName: string|null, productName: string|null, price: number|null, currency: string|null}>}
 */
function flattenSelectionsForSummary(selections, currency, product = null, optionPriceMap = null, listPriceMap = null) {
  const items = [];

  if (!selections) return items;

  // Cover first — price is stored as salePrice on the cover selection object
  const cover = selections.cover;

  if (cover) {
    // Prefer the after-promotion effective price from the fetched map so the expanded
    // row matches the set-card total; fall back to the cover's seeded sale/list price.
    const mappedCover =
      cover.productId && optionPriceMap && optionPriceMap[cover.productId] != null ?
        optionPriceMap[cover.productId] :
        null;
    const coverSale = mappedCover ?? cover.salePrice ?? cover.price ?? null;
    const mappedCoverList =
      cover.productId && listPriceMap && listPriceMap[cover.productId] != null ?
        listPriceMap[cover.productId] :
        cover.price;
    const coverList = mappedCoverList != null && coverSale != null && mappedCoverList > coverSale ?
      mappedCoverList : null;

    items.push({
      key: 'cover',
      thumbnail: cover.imageUrl ?? cover.thumbnail ?? null,
      recommendationName: null,
      productName: cover.name ?? null,
      productId: cover.productId ?? null,
      sfccRole: 'variant',
      price: coverSale,
      listPrice: coverList,
      currency,
    });
  }

  // Option containers: each value is an array of slot selections
  Object.entries(selections).forEach(([stepId, slotSelections]) => {
    if (stepId === 'cover') return;
    // Optional add-ons and KingGuard are excluded from the "Customize your own" trigger summary.
    // They live under a dedicated key and are tracked separately for add-to-cart.
    if (stepId === 'optionalAddOns') return;
    if (!Array.isArray(slotSelections)) return;

    slotSelections.forEach((slot, idx) => {
      if (!slot) return;
      // Skip silently auto-selected hidden slots — they have no name/price and are
      // invisible to the user; they exist only for cart purposes.
      if (slot.isAutoSelected) return;
      // Prefer the after-promotion effective price from the fetched map (matches the
      // set-card total + CTA); fall back to the slot's seeded sale-book price.
      const mappedPrice =
        slot.productId && optionPriceMap && optionPriceMap[slot.productId] != null ?
          optionPriceMap[slot.productId] :
          null;
      const slotPrice = mappedPrice ?? slot.price ?? null;
      const slotListRaw =
        slot.listPrice ??
        (slot.productId && listPriceMap && listPriceMap[slot.productId] != null ?
          listPriceMap[slot.productId] :
          null);
      const slotList = slotListRaw != null && slotListRaw > (slotPrice ?? 0) ? slotListRaw : null;

      items.push({
        key: `${stepId}-${idx}`,
        thumbnail: slot.imageUrl ?? slot.thumbnail ?? null,
        recommendationName:
          slot.recommendationName ??
          slot.slotLabel ??
          resolveRecommendationNameFromProduct(product, stepId, idx) ??
          null,
        productName: slot.name ?? null,
        productId: slot.productId ?? null,
        sfccRole: 'option',
        price: slotPrice,
        listPrice: slotList,
        currency,
      });
    });
  });

  return items;
}

/**
 * Renders the selected products summary below the customize-your-own trigger card header.
 * Shown only when committedSelection.mode === 'customize' and there are selections.
 *
 * @param {Object} props
 * @param {Array} props.items - Flattened list of selected items
 * @param {number} [props.total] - Optional total sale price (shown above items when provided)
 * @param {number} [props.listTotal] - Optional total list price (strikethrough when > total)
 * @param {string|null} [props.currency] - Currency code for the total row
 * @param {Object} props.styles - Theme styles from useMultiStyleConfig
 */
function SelectedProductsSummary({currency, items, listTotal, styles, total}) {
  const intl = useIntl();
  const currencyCode = currency || 'AUD';
  // Sale vs list: show struck-through list only when the configure total is below list.
  const showListTotal = listTotal > 0 && listTotal > (total ?? 0);

  return (
    <Box {...styles.selectedSummaryWrapper} className="customize-your-own-trigger-selected-products">
      {/* Total row — only for Customize Your Own, not for set card previews */}
      {total != null && (
        <Box {...styles.selectedSummaryTotalRow}>
          <Text {...styles.selectedSummaryTotalLabel}>
            <FormattedMessage defaultMessage="Total" id="choose_your_sets.summary.total" />
          </Text>
          <Box textAlign="right">
            <Text {...styles.selectedSummaryTotalPrice}>
              {formatCurrencyHideTrailingZeroCents(intl, total, currencyCode)}
            </Text>
            {showListTotal && (
              <Text color="greyscale.70" fontSize="xs" textDecoration="line-through">
                {formatCurrencyHideTrailingZeroCents(intl, listTotal, currencyCode)}
              </Text>
            )}
          </Box>
        </Box>
      )}

      {/* Item rows */}
      {items.map((item) => (
        <Box
          key={item.key}
          className="selected-customize-item"
          {...styles.selectedSummaryItemRow}
          {...sfccProductIdDataAttrs(item.sfccRole, item.productId)}
        >
          {/* Thumbnail */}
          {item.thumbnail ? (
            <Image
              alt={item.productName || ''}
              htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
              htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
              src={item.thumbnail}
              {...styles.selectedSummaryThumbnail}
            />
          ) : (
            <Skeleton {...styles.selectedSummaryThumbnail} />
          )}

          {/* Name (with optional slot label above) */}
          <Box className="selected-customize-item__info" {...styles.selectedSummaryItemInfo}>
            {item.recommendationName ? (
              <Text {...styles.selectedSummarySlotLabel}>
                {formatCustomizeSummaryLabel(item.recommendationName)}
              </Text>
            ) : null}
            <Text {...styles.selectedSummaryItemName}>
              {formatCustomizeSummaryLabel(item.productName)}
            </Text>
          </Box>

          {/* Price — always shown, even if $0 */}
          <Box className="selected-customize-item__price" textAlign="right" minWidh={{base: 'auto', md: '17%'}}>
            <Text {...styles.selectedSummaryItemPrice}>
              {formatCurrencyHideTrailingZeroCents(intl, item.price || 0, item.currency || 'AUD')}
            </Text>
            {item.listPrice != null && (
              <Text color="greyscale.70" fontSize="xs" textDecoration="line-through">
                {formatCurrencyHideTrailingZeroCents(intl, item.listPrice, item.currency || 'AUD')}
              </Text>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

SelectedProductsSummary.displayName = 'SelectedProductsSummary';

SelectedProductsSummary.propTypes = {
  currency: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      currency: PropTypes.string,
      key: PropTypes.string.isRequired,
      listPrice: PropTypes.number,
      price: PropTypes.number,
      productName: PropTypes.string,
      recommendationName: PropTypes.string,
      thumbnail: PropTypes.string,
    })
  ).isRequired,
  listTotal: PropTypes.number,
  styles: PropTypes.object.isRequired,
  total: PropTypes.number,
};

/**
 * Expanded detail panel for a set card.
 * Reuses the same display logic as the "Customize Your Own" result summary
 * (flattenSelectionsForSummary + SelectedProductsSummary) for visual consistency.
 *
 * When `selections` is provided (enriched committed selections from the context),
 * it is used directly — this ensures images fetched by the enrichment effect are shown.
 * Otherwise falls back to computing selections from the raw product data.
 *
 * @param {Object} props
 * @param {Object|null} props.masterProduct - Master product with enriched OGU
 * @param {Object|null} [props.selections] - Pre-computed enriched selections (from committedSelection)
 * @param {Object|null} props.setProduct - Linked product (the set) with its own OGU
 * @param {Object} props.styles - Theme styles from useMultiStyleConfig
 * @param {Object|null} [props.optionPriceMap] - productId → after-promotion effective price
 * @param {Object|null} [props.listPriceMap] - productId → list (RRP) price
 */
function SetCardExpandedDetails({masterProduct, panelId, selections: enrichedSelections, setProduct, styles, optionPriceMap, listPriceMap}) {
  const currency = masterProduct?.currency ?? null;

  const defaultSelections = useMemo(
    () => enrichedSelections || buildDefaultSelectionsFromData(setProduct, masterProduct),
    [enrichedSelections, setProduct, masterProduct]
  );

  const summaryItems = useMemo(
    () => flattenSelectionsForSummary(defaultSelections, currency, masterProduct, optionPriceMap, listPriceMap),
    [defaultSelections, currency, masterProduct, optionPriceMap, listPriceMap]
  );

  if (summaryItems.length === 0) {
    if (!setProduct) {
      return (
        <Box id={panelId} {...styles.setExpandedWrapper}>
          <Skeleton height="14px" marginBottom="6px" width="60%" />
          <Skeleton height="14px" width="80%" />
        </Box>
      );
    }

    return null;
  }

  return (
    <Box className="set-card-expanded-details" id={panelId} {...styles.setExpandedWrapper}>
      <SelectedProductsSummary items={summaryItems} styles={styles} />
    </Box>
  );
}

SetCardExpandedDetails.displayName = 'SetCardExpandedDetails';

SetCardExpandedDetails.propTypes = {
  listPriceMap: PropTypes.object,
  masterProduct: PropTypes.object,
  optionPriceMap: PropTypes.object,
  panelId: PropTypes.string,
  selections: PropTypes.object,
  setProduct: PropTypes.object,
  styles: PropTypes.object.isRequired,
};

function SetCardItem({card, isExpanded, isSelected, listTotal, onSelect, onEdit, styles, totalPrice}) {
  const intl = useIntl();
  const cardCurrency = card.currency || 'AUD';
  const labelId = `set-card-label-${card.productId}`;
  const detailsPanelId = `set-card-details-${card.productId}`;

  const handleSelect = () => onSelect(card.productId);

  return (
    <Box
      {...styles.setCard}
      borderBottomRadius={isExpanded ? 0 : '4px'}
      className="set-card-item"
      data-testid={`set-card-${card.productId}`}
      display="grid"
      gridTemplateColumns="64px 1fr auto"
      gridTemplateRows="auto auto"
      columnGap={4}
      rowGap={2}
      alignItems="center"
      {...sfccProductIdDataAttrs('product', card.productId)}
    >
      {/* Thumbnail — click selects set; not nested inside role="radio" */}
      <Box
        {...styles.setCardImageWrapper}
        gridColumn="1"
        gridRow="1 / 3"
        position="relative"
        alignSelf="center"
        cursor="pointer"
        onClick={handleSelect}
      >
        {card.thumbnail ? (
          <Image
            alt={card.name || ''}
            htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
            htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
            src={card.thumbnail}
            {...styles.setCardImage}
          />
        ) : (
          <Skeleton height="100%" width="100%" />
        )}
        {card.badgeText && (
          <Box {...styles.setCardBadge}>
            <Text {...styles.setCardBadgeText}>{card.badgeText}</Text>
          </Box>
        )}
      </Box>

      {/* Select set — radio control (name only; Edit is a sibling below) */}
      <Box
        aria-checked={isSelected}
        {...(isExpanded ? {'aria-controls': detailsPanelId} : {})}
        aria-labelledby={labelId}
        cursor="pointer"
        gridColumn="2"
        gridRow="1"
        minWidth={0}
        role="radio"
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={(event) => handleRadioTileKeyDown(event, false, handleSelect)}
      >
        <Text id={labelId} {...styles.setCardName}>
          {card.name}
        </Text>
      </Box>

      {/* Edit — separate focusable control, not nested in role="radio" */}
      <Text
        {...styles.setCardEditLink}
        aria-label={intl.formatMessage(
          {defaultMessage: 'Edit {setName}', id: 'choose_your_sets.card.edit_button_a11y'},
          {setName: card.name || ''}
        )}
        as="button"
        gridColumn="2"
        gridRow="2"
        justifySelf="start"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit(card.productId);
        }}
      >
        <FormattedMessage defaultMessage="Edit" id="choose_your_sets.card.edit_button" />
      </Text>

      {/* Price + chevron — click selects set */}
      <Flex
        {...styles.setCardPriceRow}
        alignSelf="center"
        cursor="pointer"
        gridColumn="3"
        gridRow="1 / 3"
        onClick={handleSelect}
      >
        {(() => {
          const displayPrice = totalPrice ?? card.price;
          const originalValue = listTotal > 0 ? listTotal : card.originalPrice;
          const showOriginal = originalValue != null && originalValue > (displayPrice ?? 0);

          return (displayPrice != null || showOriginal) ? (
            <Box {...styles.setCardPriceColumn}>
              {displayPrice != null && (
                <Text {...styles.setCardSalePrice}>
                  {formatCurrencyHideTrailingZeroCents(intl, displayPrice, cardCurrency)}
                </Text>
              )}
              {showOriginal && (
                <Text {...styles.setCardOriginalPrice}>
                  {formatCurrencyHideTrailingZeroCents(intl, originalValue, cardCurrency)}
                </Text>
              )}
            </Box>
          ) : null;
        })()}
        <Box
          {...styles.setCardChevron}
          aria-hidden="true"
          transform={isExpanded ? 'rotate(180deg)' : 'none'}
          transition="transform 0.2s ease"
        >
          <svg
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14.2997 4.13326C14.534 3.89898 14.9138 3.89895 15.1481 4.13326C15.3824 4.36756 15.3824 4.7474 15.1481 4.98169L8.845 11.2848L8.84421 11.284C8.74474 11.3864 8.62688 11.4694 8.49578 11.5262C8.35888 11.5855 8.21057 11.6161 8.0614 11.6161C7.91233 11.616 7.7646 11.5854 7.62781 11.5262C7.49649 11.4694 7.37816 11.3867 7.27859 11.284V11.2848L0.975465 4.98169C0.741201 4.74743 0.741302 4.36758 0.975465 4.13326C1.20978 3.89894 1.58959 3.89894 1.8239 4.13326L8.0614 10.3708L14.2997 4.13326Z"
              fill="currentColor"
            />
          </svg>
        </Box>
      </Flex>
    </Box>
  );
}

SetCardItem.displayName = 'SetCardItem';

SetCardItem.propTypes = {
  card: PropTypes.shape({
    badgeText: PropTypes.string,
    currency: PropTypes.string,
    name: PropTypes.string,
    originalPrice: PropTypes.number,
    price: PropTypes.number,
    productId: PropTypes.string.isRequired,
    thumbnail: PropTypes.string,
  }).isRequired,
  isExpanded: PropTypes.bool,
  isSelected: PropTypes.bool,
  listTotal: PropTypes.number,
  onEdit: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
  totalPrice: PropTypes.number,
};

/**
 * ChooseYourSets renders:
 * 1. A list of pre-configured set cards (from context setCards / productLinks cross_sell).
 * 2. A "Customize your own" trigger card at the bottom that opens the flyout on masterA.
 *
 * When no set cards are available, only the "Customize your own" trigger is shown
 * (preserving the original behaviour).
 *
 * Set cards can be expanded/collapsed to preview included cover and option selections.
 * Selecting a set card commits it to the journey state and updates the gallery.
 * The "Edit" link on each card opens the customize flyout seeded with the set's defaults.
 *
 * @param {Object} props
 * @param {Object} props.product - Master product (masterA)
 * @param {Function} [props.onOpenCustomize] - Called to open the customize flyout.
 *   Receives the productId to edit (setId or masterProduct.id).
 */
export function ChooseYourSets({onOpenCustomize, product, isComplexProduct}) {
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const {
    committedSelection,
    editSet,
    flyoutDraftState,
    isLoadingSetCards,
    openFlyout,
    selectSet,
    setCards,
    setCardTotals,
    setCardListTotals,
    setProductsMap,
    setOptionPriceMap,
    setOptionListPriceMap,
    selectSetSafe,
    openFlyoutSafe,
    editSetSafe,
  } = useCustomizeJourney();

  const [expandedSetId, setExpandedSetId] = useState(null);

  const {isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose} = useDisclosure();
  const [pendingAction, setPendingAction] = useState(null);

  // Auto-expand/collapse set cards when the flyout closes after Done.
  // useLayoutEffect so the expanded/collapsed DOM (and totals height) is settled before paint and
  // before PDP post-close scroll (usePdpCustomizeMirrors) measures the anchor in a rAF.
  const prevFlyoutOpen = useRef(flyoutDraftState.isOpen);

  useLayoutEffect(() => {
    const wasFlyoutOpen = prevFlyoutOpen.current;

    prevFlyoutOpen.current = flyoutDraftState.isOpen;

    if (wasFlyoutOpen && !flyoutDraftState.isOpen) {
      if (committedSelection.mode === 'set' && committedSelection.productId) {
        setExpandedSetId(committedSelection.productId);
      } else {
        setExpandedSetId(null);
      }
    }
  }, [flyoutDraftState.isOpen, committedSelection.mode, committedSelection.productId]);

  useEffect(() => {
    if (committedSelection.mode === 'set' && committedSelection.productId) {
      setExpandedSetId(committedSelection.productId);

      return;
    }

    setExpandedSetId(null);
  }, [committedSelection.mode, committedSelection.productId]);

  // Contentful fields for the "Customize your own" trigger card.
  const contentfulData = useProductDetailContentful();
  const customizeContent = contentfulData?.customizeContent;
  const customizeImageUrl = contentfulData?.customizeImageBackground?.url;
  const warningMessageSwitchCustomizeMode = contentfulData?.warningMessageSwitchCustomizeMode;

  const optionsGroupingUI = getOptionsGroupingUIProcessed(product);
  const coverGroups = getCoverGroups(product);
  const hasCoverData = coverGroups.length > 0;
  const hasOptionsData = optionsGroupingUI && optionsGroupingUI.length > 0;

  // Currency from product — reused for cover and all option items
  const currency = product?.currency ?? null;
  const coverVariant = useCommittedCoverVariantProduct(product);

  // Flatten committed selections into summary items (only when customize mode is active).
  // The "Customize Your Own" summary shows after the user completes the customize panel
  // (Done), not when simply selecting a set card. Deviation from a set → mode='customize'.
  const isCustomizeMode = committedSelection.mode === 'customize';

  /** Visible line rows for SelectedProductsSummary only. Excludes hidden isAutoSelected OGU and optionalAddOns — not the pricing total. */
  const summaryItems = useMemo(() => {
    if (!isCustomizeMode) return [];

    return flattenSelectionsForSummary(committedSelection.selections, currency, product, setOptionPriceMap, setOptionListPriceMap);
  }, [isCustomizeMode, committedSelection.selections, currency, product, setOptionPriceMap, setOptionListPriceMap]);

  /** Commerce total for the Total row — computeCustomizeTotal (same as ComplexTotalPrice, ATC strip, cart). Includes hidden slots and add-ons; not sum(summaryItems). */
  const summaryTotal = useMemo(() => {
    if (!isCustomizeMode) return 0;

    return computeCustomizeTotal(
      committedSelection.selections,
      coverVariant,
      true,
      null,
      setOptionPriceMap
    );
  }, [
    isCustomizeMode,
    committedSelection.selections,
    coverVariant,
    setOptionPriceMap,
  ]);

  /** List (RRP) total for strikethrough on Total row — computeCustomizeListTotal; same scope as summaryTotal, list prices only. */
  const summaryListTotal = useMemo(() => {
    if (!isCustomizeMode) return 0;

    return computeCustomizeListTotal(
      committedSelection.selections,
      coverVariant,
      true,
      null,
      setOptionListPriceMap
    );
  }, [
    isCustomizeMode,
    committedSelection.selections,
    coverVariant,
    setOptionListPriceMap,
  ]);
  const hasSummary = isCustomizeMode && summaryItems.length > 0;

  const hasSetCards = setCards.length > 0;

  // --- Handlers ---

  const handleSelectSet = useCallback(
    (setId) => {
      const alreadyThisSet =
        committedSelection.mode === 'set' &&
        committedSelection.productId === setId;

      if (alreadyThisSet) {
        setExpandedSetId((prev) => (prev === setId ? null : setId));

        return;
      }

      const result = selectSetSafe(setId, () => {
        setPendingAction({type: 'selectSet', id: setId});
        onConfirmOpen();
      });

      if (!result.needsConfirm) {
        setExpandedSetId((prev) => (prev === setId ? null : setId));
      }
    },
    [committedSelection.mode, committedSelection.productId, selectSetSafe, onConfirmOpen]
  );

  const handleEditSet = useCallback(
    (setId) => {
      const result = editSetSafe(setId, () => {
        setPendingAction({type: 'editSet', id: setId});
        onConfirmOpen();
      });

      if (!result.needsConfirm) {
        onOpenCustomize?.();
      }
    },
    [editSetSafe, onConfirmOpen, onOpenCustomize]
  );

  const handleCustomizeYourOwn = useCallback(() => {
    const masterProductId = product?.id ?? product?.productId;

    const result = openFlyoutSafe(masterProductId, () => {
      setPendingAction({type: 'customize', id: masterProductId});
      onConfirmOpen();
    });

    if (!result.needsConfirm) {
      onOpenCustomize?.(masterProductId);
    }
  }, [openFlyoutSafe, onConfirmOpen, onOpenCustomize, product]);

  const handleConfirm = useCallback(() => {
    if (!pendingAction) return;

    switch (pendingAction.type) {
      case 'selectSet':
        selectSet(pendingAction.id);
        setExpandedSetId((prev) => (prev === pendingAction.id ? null : pendingAction.id));
        break;
      case 'editSet':
        editSet(pendingAction.id);
        onOpenCustomize?.();
        break;
      case 'customize':
        openFlyout(pendingAction.id);
        onOpenCustomize?.(pendingAction.id);
        break;
    }

    setPendingAction(null);
    onConfirmClose();
  }, [pendingAction, selectSet, editSet, openFlyout, onOpenCustomize, onConfirmClose]);

  const handleCancel = useCallback(() => {
    setPendingAction(null);
    onConfirmClose();
  }, [onConfirmClose]);

  if ((!hasCoverData && !hasOptionsData) || !onOpenCustomize) {
    return null;
  }

  const title = product?.name ?? '';
  const imageUrl = customizeImageUrl || null;

  return (
    <Box className="choose-your-sets-container" {...styles.container}>
      {/* Section 1: Choose from popular sets */}
      {(hasSetCards || isLoadingSetCards) && (
        <Box
          aria-labelledby="choose-your-sets-heading"
          className="choose-your-sets-list"
          id="choose-your-sets-anchor"
          role="radiogroup"
          sx={{scrollMarginTop: '10px'}}
        >
          <Text id="choose-your-sets-heading" {...styles.popularSetsHeading}>
            <FormattedMessage
              defaultMessage="Choose from popular sets"
              id="choose_your_sets.heading"
            />
          </Text>

          {isLoadingSetCards && !hasSetCards ? (
            <Box className="choose-your-sets-skeletons">
              {[1, 2, 3].map((i) => (
                <Skeleton
                  key={i}
                  borderRadius="4px"
                  height="76px"
                  marginBottom="8px"
                  width="100%"
                />
              ))}
            </Box>
          ) : (
            setCards.map((card) => {
              const isSelected =
                committedSelection.mode === 'set' &&
                committedSelection.productId === card.productId;
              const isExpanded = expandedSetId === card.productId;
              const detailsPanelId = `set-card-details-${card.productId}`;

              return (
                <Box
                  key={card.productId}
                  className="set-card-wrapper"
                  {...styles.setCardWrapper}
                  data-selected={isSelected ? 'true' : 'false'}
                >
                  <SetCardItem
                    card={card}
                    isExpanded={isExpanded}
                    isSelected={isSelected}
                    listTotal={setCardListTotals?.[card.productId]}
                    styles={styles}
                    totalPrice={setCardTotals[card.productId]}
                    onEdit={handleEditSet}
                    onSelect={handleSelectSet}
                  />
                  {isExpanded && (
                    <SetCardExpandedDetails
                      listPriceMap={setOptionListPriceMap}
                      masterProduct={product}
                      optionPriceMap={setOptionPriceMap}
                      panelId={detailsPanelId}
                      selections={isSelected ? committedSelection.selections : undefined}
                      setProduct={setProductsMap?.[card.productId]}
                      styles={styles}
                    />
                  )}
                </Box>
              );
            })
          )}
        </Box>
      )}

      {/* Divider: "Or customize your own" — shown only when there are set cards (Complex PDP only) */}
      {hasSetCards && isComplexProduct && (
        <Flex alignItems="center" className="choose-your-sets-divider" gap={4} marginY={8}>
          <Divider borderColor="rgba(27,26,24,0.1)" flex={1} />
          <Text
            color="greyscale.90"
            flexShrink={0}
            fontSize={{base: 'sm', lg: 'md'}}
            fontWeight="400"
            lineHeight="1.4"
            whiteSpace="nowrap"
          >
            <FormattedMessage
              defaultMessage="Or customise your own"
              id="product_view.variation.or_customize_your_own"
            />
          </Text>
          <Divider borderColor="rgba(27,26,24,0.1)" flex={1} />
        </Flex>
      )}

      {/* Section 2: Customize your own — anchor scrolls into view after customize flyout Done */}
      {isComplexProduct && (
        <Box
          className="customize-your-own-anchor"
          data-testid="customize-your-own-anchor"
          id="customize-your-own-anchor"
          sx={{scrollMarginTop: {base: '20px', lg: '10px'}}}
        >
          <Box
            {...styles.customizeTriggerCard}
            aria-pressed={isCustomizeMode}
            borderColor={isCustomizeMode ? 'brandColors.greyscale90' : 'rgba(27,26,24,0.1)'}
            borderStyle="solid"
            borderWidth={isCustomizeMode ? '2px' : '1px'}
            className="customize-your-own-trigger"
            data-testid="customize-your-set-btn"
            flexDirection="column"
            role="button"
            sx={{
              boxShadow: 'sm',
              outline: 'none',
              transition: 'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
              _hover: {
                bg: 'gray.50',
                borderColor: isCustomizeMode ? 'brandColors.greyscale90' : 'rgba(27,26,24,0.1)',
                borderStyle: 'solid',
                borderWidth: isCustomizeMode ? '2px' : '1px',
                boxShadow: 'sm',
              },
              _focus: A11Y_FOCUS_VISIBLE._focus,
              _focusVisible: {
                ...A11Y_FOCUS_VISIBLE._focusVisible,
                borderColor: isCustomizeMode ? 'brandColors.greyscale90' : 'rgba(27,26,24,0.1)',
                borderStyle: 'solid',
                borderWidth: isCustomizeMode ? '2px' : '1px',
              },
            }}
            tabIndex={0}
            onClick={handleCustomizeYourOwn}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCustomizeYourOwn();
              }
            }}
          >
            {/* Top section: text content + product image */}
            <Flex className="customize-your-own-trigger-content-wrapper" width="100%">
              {/* Left: text content */}
              <Box className="customize-your-own-trigger-content" {...styles.customizeTriggerContent}>
                <Box {...styles.customizeTriggerTitle}>
                  {customizeContent ? (
                    <RichTextContent
                      content={customizeContent}
                      {...styles.customizeTriggerDescription}
                    />
                  ) : null}
                </Box>
                <Flex alignItems="center" gap="8px" marginTop={2}>
                  <Text {...styles.customizeTriggerLink}>
                    <FormattedMessage
                      defaultMessage="Customise"
                      id="product_view.variation.customize_button"
                    />
                  </Text>
                  <ChevronRightCustomizeIcon
                    aria-hidden
                    boxSize="12px"
                    color="brandColors.primaryDarkLinen"
                    flexShrink={0}
                    minH="unset"
                    minW="unset"
                  />
                </Flex>
              </Box>

              {/* Right: product image */}
              {imageUrl ? (
                <Box className="customize-your-own-trigger-image-wrapper" {...styles.customizeTriggerImageWrapper}>
                  <Image
                    alt={title}
                    htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                    htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                    src={imageUrl}
                    {...styles.customizeTriggerImage}
                  />
                </Box>
              ) : null}
            </Flex>

            {/* Bottom section: selected products summary (shown after "Done") */}
            {hasSummary ? (
              <SelectedProductsSummary
                currency={currency}
                items={summaryItems}
                listTotal={summaryListTotal}
                styles={styles}
                total={summaryTotal}
              />
            ) : null}
          </Box>
        </Box>
      )}

      <ModeChangeConfirmationDialog
        contentfulContent={warningMessageSwitchCustomizeMode}
        isOpen={isConfirmOpen}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </Box>
  );
}

ChooseYourSets.displayName = 'ChooseYourSets';

ChooseYourSets.propTypes = {
  onOpenCustomize: PropTypes.func,
  product: PropTypes.object,
  isComplexProduct: PropTypes.bool,
};
